import { produce } from "immer";
import _ from "lodash";
import { toggleInArray } from "src/util/functions";
import { formatPhoneNumber } from "src/util/gFormatting";
import { makeFinalDRAFT, RecursivePartial } from "src/util/types";
import { getDefaultApplication } from "./defaultData";
import {
  tApplicationActions,
  tApplicationState,
  tApplicationState_DRAFT,
  tDisableEmail,
  tDisableHearAboutUs,
  tSetIsShown,
  tToggleRepCategoryAction,
  tUpdateApplication,
  tUpdateContactPhoneApplication,
  tUpdateRepCategories,
  tUpdateType,
} from "./types";

export const handleUpdate = (draft: tApplicationState_DRAFT, action: tUpdateApplication): tApplicationState_DRAFT => {
  if (action.reset === true) {
    return _.merge<tApplicationState_DRAFT, tUpdateApplication["applicationPart"]>(
      getDefaultApplication("brand"),
      action.applicationPart,
    );
  }
  return _.merge<tApplicationState_DRAFT, tUpdateApplication["applicationPart"]>(draft, action.applicationPart);
};

export const handleUpdatePhoneNumber = (
  draft: tApplicationState_DRAFT,
  action: tUpdateContactPhoneApplication,
): tApplicationState_DRAFT => {
  draft.contact.phone = formatPhoneNumber(action.phoneNumber);
  return draft;
};

type tpHandleToggleRepCategory = (
  draft: tApplicationState_DRAFT,
  action: tToggleRepCategoryAction,
) => tApplicationState_DRAFT;
export const handleToggleRepCategory: tpHandleToggleRepCategory = (draft, action) => {
  if (draft.type === "rep" && draft.category.length < 3) {
    draft.category = toggleInArray(draft.category, action.category);
  } else if (draft.type === "rep" && draft.category.includes(action.category)) {
    draft.category = toggleInArray(draft.category, action.category);
  }
  return draft;
};

export const handleIsShown = (draft: tApplicationState_DRAFT, action: tSetIsShown): tApplicationState_DRAFT => {
  draft.isShown = action.isShown;
  return draft;
};

export const handleDisableEmail = (draft: tApplicationState_DRAFT, action: tDisableEmail): tApplicationState_DRAFT => {
  draft.emailDisabled = action.emailDisabled;
  return draft;
};

export const handleDisableHearAboutUs = (
  draft: tApplicationState_DRAFT,
  action: tDisableHearAboutUs,
): tApplicationState_DRAFT => {
  draft.hearAboutUsDisabled = action.payload;
  return draft;
};

export const handleUpdateType = (draft: tApplicationState_DRAFT, action: tUpdateType): tApplicationState_DRAFT => {
  if (draft.type !== action.newType) {
    const aBlankState = getDefaultApplication(action.newType);
    return _.merge<tApplicationState_DRAFT, RecursivePartial<tApplicationState_DRAFT>>(aBlankState, {
      contact: draft.contact,
      address: draft.address,
      note: draft.note,
      website: draft.website,
    });
  } else {
    return draft;
  }
};

export const handleRepCategory = (
  draft: tApplicationState_DRAFT,
  action: tUpdateRepCategories,
): tApplicationState_DRAFT => {
  if (draft.type !== "rep") {
    return draft;
  }
  const tempCategories = draft.category;
  if (tempCategories.includes(action.category)) {
    draft.category = tempCategories.filter((myCategory) => myCategory !== action.category);
  } else if (tempCategories.length < 3) {
    tempCategories.push(action.category);
  }
  return draft;
};

// Inspiration from: https://adostes.medium.com/simplify-redux-reducers-with-immer-9a3638229d45
export const ApplicationReducer = produce(
  (draft: tApplicationState_DRAFT = getDefaultApplication("brand"), action: tApplicationActions): tApplicationState => {
    switch (action.type) {
      case "UPDATE_TYPE":
        return makeFinalDRAFT(handleUpdateType(draft, action));
      case "UPDATE_CONTACT_APPLICATION":
        return makeFinalDRAFT(handleUpdate(draft, action));
      case "UPDATE_PHONE_APPLICATION":
        return makeFinalDRAFT(handleUpdatePhoneNumber(draft, action));
      case "UPDATE_BRAND_APPLICATION":
        return makeFinalDRAFT(handleUpdate(draft, action));
      case "UPDATE_REP_APPLICATION":
        return makeFinalDRAFT(handleUpdate(draft, action));
      case "UPDATE_RETAIL_APPLICATION":
        return makeFinalDRAFT(handleUpdate(draft, action));
      case "APPLICATION_SHOW_VALIDATION":
        return makeFinalDRAFT(handleIsShown(draft, action));
      case "DISABLE_EMAIL":
        return makeFinalDRAFT(handleDisableEmail(draft, action));
      case "DISABLE_HEAR_ABOUT_US":
        return makeFinalDRAFT(handleDisableHearAboutUs(draft, action));
      case "UPDATE_REP_CATEGORIES":
        return makeFinalDRAFT(handleRepCategory(draft, action));
      case "TOGGLE_REP_APPLICATION_CATEGORY":
        return makeFinalDRAFT(handleToggleRepCategory(draft, action));
      case "APPLICATION_RESET":
        return makeFinalDRAFT(getDefaultApplication("brand"));
      default:
        return makeFinalDRAFT(draft); // todo:  Why is this called 24 times on startup!!!!
    }
  },
);
